import type { ReactNode } from 'react';
import { cx } from '../cx';

export type CodeBlockProps = {
  /** The code, shown exactly as written (whitespace and line breaks kept). */
  code: string;
  /** A caption above the block ("robots.txt"). */
  label?: ReactNode;
  /** Accessible name for the scrollable region when there is no visible label. */
  'aria-label'?: string;
  className?: string;
};

/**
 * A multi-line, read-only code sample for documents: user-agent strings, robots.txt rules, config
 * snippets. Sits on the elevated ground in ink (never a black panel), scrolls sideways instead of
 * wrapping, and is focusable so keyboard users can scroll it. Hook-free, so it is safe on pages that
 * do not hydrate. For a single value with a copy button use CodeField.
 */
export function CodeBlock({ code, label, 'aria-label': ariaLabel, className }: CodeBlockProps) {
  return (
    <figure className={cx('flex flex-col gap-1.5', className)}>
      {label ? <figcaption className="type-caption text-jc-ink-secondary">{label}</figcaption> : null}
      <pre
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={0}
        aria-label={label ? undefined : ariaLabel}
        className="overflow-x-auto rounded-jc-field bg-jc-elevated px-4 py-3 font-mono text-sm text-jc-ink ring-1 ring-inset ring-jc-border-control focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jc-focus"
      >
        <code>{code}</code>
      </pre>
    </figure>
  );
}
